import React from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import Dialog, { DialogTitle, DialogContent, DialogActions } from "components/common/Dialog";
import Button from "components/common/Button";
import moment from "moment";
import "./TimesheetTab.scss";

function TimesheetDetailModal({ open, onClose, timesheetData }) {
  if (!timesheetData || !timesheetData.clockinDetails) return null;

  const dayName = moment(timesheetData.date).format("dddd");
  const dateFormatted = moment(timesheetData.date).format("MMM DD, YYYY");
  const shiftTime = timesheetData.shift || "No shift scheduled";
  const { clockinDetails } = timesheetData;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="lg"
      width="780px"
      title={
        <div className="clockin-modal-title">
          <DialogTitle
            title={
              <div className="detail-header">
                <p className="heading-h2-semibold">
                  <span className="heading-h2-semibold">{dayName},</span> {dateFormatted}
                </p>
                <p className="para-body-m-regular text-body-primary">Shift time: {shiftTime}</p>
              </div>
            }
          />
        </div>
      }
      content={
        <DialogContent>
          <Box className="timesheet-detail-modal">
            <p className="heading-h4-medium summary-section-title">Clock in and out summary</p>
            <div className="summary-cards">
              <div className="summary-card">
                <div className="card-content">
                  <p className="card-label para-body-m-regular">Status</p>
                  <p className="card-value heading-h4-semibold">{clockinDetails.status}</p>
                </div>
              </div>
              <div className="summary-card">
                <div className="card-content">
                  <p className="card-label para-body-m-regular">Clock in and out time</p>
                  <p className="card-value heading-h4-semibold">{clockinDetails.clockInOutTime}</p>
                </div>
              </div>
              <div className="summary-card highlighted">
                <div className="card-content">
                  <p className="card-label para-body-m-regular">Total net work duration</p>
                  <p className="card-value heading-h4-semibold">{clockinDetails.totalNetWorkDuration}</p>
                </div>
              </div>
            </div>

            <div className="breakdown-section">
              <p className="heading-h4-medium">Breakdown</p>
              <div className="breakdown-table">
                <table>
                  <thead>
                    <tr>
                      <th aria-label="Type" />
                      <th className="heading-h5-medium">Start time</th>
                      <th className="heading-h5-medium">End time</th>
                      <th className="heading-h5-medium">Total duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clockinDetails.breakdown.map((row) => (
                      <tr
                        key={`${row.label}-${row.startTime}-${row.endTime}`}
                        className={row.label === "Work" ? "work-row" : "break-row"}
                      >
                        <td className="row-label para-body-m-medium">{row.label}</td>
                        <td className="para-body-m-regular">{row.startTime}</td>
                        <td className="para-body-m-regular">
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {row.endTime}
                            {row.isNextDay && <span className="next-day-badge">Next day</span>}
                          </Box>
                        </td>
                        <td className="para-body-m-regular">{row.duration}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="note-text para-body-m-medium">Note: All times are in local timezone</p>
            </div>
          </Box>
        </DialogContent>
      }
      actions={
        <DialogActions>
          <Button label="Done" variant="contained" color="primary" size="large" onClick={onClose} />
        </DialogActions>
      }
    />
  );
}

TimesheetDetailModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  timesheetData: PropTypes.shape({
    date: PropTypes.string,
    shift: PropTypes.string,
    clockinDetails: PropTypes.shape({
      status: PropTypes.string,
      clockInOutTime: PropTypes.string,
      totalNetWorkDuration: PropTypes.string,
      breakdown: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string,
          startTime: PropTypes.string,
          endTime: PropTypes.string,
          duration: PropTypes.string,
          isNextDay: PropTypes.bool,
        })
      ),
    }),
  }),
};

export default TimesheetDetailModal;
